import { motion } from "framer-motion";
import { Check, X as XIcon, ArrowRight } from "lucide-react";
import { AnimatedSection } from "../shared";

const COMPARISON_ROWS = [
  { bad: "Manual server", good: "Auto-provisioned" },
  { bad: "Docker / CLI", good: "Browser app" },
  { bad: "You manage uptime", good: "We handle infra" },
  { bad: "BYO API keys", good: "Pre-configured" },
  { bad: "No financial layer", good: "Neobank built-in" },
] as const;

export function WhyAgntHub() {
  return (
    <section className="py-12 md:py-16 px-6 relative overflow-hidden bg-[#f5f5f5]">
      <div className="max-w-7xl mx-auto w-full relative z-10">
        <AnimatedSection>
          <div className="mb-12">
            <span className="inline-flex items-center gap-2.5 text-[11px] font-semibold tracking-[0.2em] uppercase text-black/40 mb-5 before:content-[''] before:w-5 before:h-px before:bg-gradient-to-r before:from-black/30 before:to-transparent">
              Why AGNT.Hub
            </span>
            <h2 className="font-space-grotesk text-4xl md:text-6xl font-bold max-w-4xl tracking-[-0.03em] leading-[1.1] text-black">
              The personal AI assistant trend is here, but setup is the
              bottleneck.
            </h2>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          <AnimatedSection delay={0.1} className="lg:col-span-2">
            <div className="h-full bg-white border border-black/[0.08] p-3 md:p-10 rounded-2xl">
              <div className="flex flex-col gap-6 h-full">
                <h3 className="font-space-grotesk text-2xl md:text-3xl font-bold leading-snug text-black">
                  AI agents are the next execution layer. But most people can't
                  use them.
                </h3>
                <p className="text-black/60 text-base leading-relaxed">
                  Self-hosted agents require servers, Docker, API keys, and
                  infrastructure. It's built for developers, not for everyone.
                </p>
                <div className="mt-auto p-6 bg-black/[0.03] rounded-xl border border-black/[0.06]">
                  <p className="text-lg font-bold font-space-grotesk text-black/80">
                    No self-hosting. No config. Just agents that work.
                  </p>
                </div>
              </div>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.25} className="lg:col-span-3">
            <div className="h-full bg-white border border-black/[0.08] rounded-2xl p-3 md:p-10 relative overflow-hidden">
              <div className="relative z-10">
                <div className="grid grid-cols-[1fr_auto_1fr] gap-2 md:gap-4 mb-8 text-[10px] font-bold uppercase tracking-[0.15em] text-black/50 border-b border-black/[0.08] pb-5">
                  <div>Self-Hosted</div>
                  <div className="w-6"></div>
                  <div className="text-black/80">AGNT.Hub</div>
                </div>

                <div className="flex flex-col gap-5 text-[15px]">
                  {COMPARISON_ROWS.map((row, i) => (
                    <motion.div
                      key={row.bad}
                      className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 md:gap-4"
                      initial={{ opacity: 0, x: -15 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: 0.08 * i }}
                    >
                      <div className="text-black/55 flex items-center md:gap-2.5 gap-2">
                        <div className="w-5 h-5 rounded-full bg-black/[0.05] shrink-0 flex items-center justify-center">
                          <XIcon className="w-3 h-3 text-black/40" />
                        </div>
                        {row.bad}
                      </div>
                      <ArrowRight className="text-black/10 w-4 h-4" />
                      <div className="font-medium text-black/80 flex items-center md:gap-2.5 gap-2">
                        <motion.div
                          className="w-5 h-5 rounded-full shrink-0 bg-black/10 flex items-center justify-center"
                          initial={{ scale: 0 }}
                          whileInView={{ scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.2 + 0.08 * i, type: "spring" }}
                        >
                          <Check className="w-3 h-3 text-black/70" />
                        </motion.div>
                        {row.good}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}

export default WhyAgntHub;
